import { type B2bScheme, newB2bSchemeVersion, type SchemeDraftInput } from '@api/b2b';
import { todayIst } from './utils';

/** A scheme being edited: `id` null for a new one. */
export interface SchemeEditing {
    id: string | null;
    draft: SchemeDraftInput;
}

/** Only the fields a draft accepts: the API rejects any other property on the body. */
export const pickDraft = (s: SchemeDraftInput): SchemeDraftInput => ({
    code: s.code,
    name: s.name,
    effectiveFrom: s.effectiveFrom,
    ...(s.effectiveTo ? { effectiveTo: s.effectiveTo } : {}),
    appliesToAll: s.appliesToAll,
    customerIds: s.customerIds,
    segments: s.segments,
    tags: s.tags,
    priority: s.priority,
    exclusivity: s.exclusivity,
    slabBasis: s.slabBasis,
    benefitType: s.benefitType,
    rule: s.rule,
});

export const blankDraft = (patch: Partial<SchemeDraftInput> = {}): SchemeDraftInput => ({
    code: '',
    name: '',
    effectiveFrom: todayIst(),
    appliesToAll: true,
    customerIds: [],
    segments: [],
    tags: [],
    priority: 100,
    exclusivity: 'NONE',
    slabBasis: 'QTY',
    benefitType: 'FREE_QTY',
    rule: { slabs: [{ min: 1, max: null, buy: 10, get: 1 }], productScope: { categories: [], productIds: [] }, freePool: { type: 'SAME_ITEM', productIds: [] } },
    ...patch,
});

/**
 * What editing a scheme opens: a draft as it is. A live scheme is never changed in place (orders
 * keep what they earned), so it opens its pending draft version, or starts one; publishing that
 * draft replaces the live version.
 */
export async function editingFor(scheme: B2bScheme, schemes: B2bScheme[]): Promise<SchemeEditing> {
    const draft =
        scheme.status === 'DRAFT' ? scheme : (schemes.find((x) => x.code === scheme.code && x.status === 'DRAFT') ?? (await newB2bSchemeVersion(scheme.id)));
    return { id: draft.id, draft: pickDraft({ ...draft, effectiveTo: draft.effectiveTo ?? undefined }) };
}
